import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";
import { Tally3Icon } from "lucide-react";

import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/css";

const sliderVariants = cva(
  "relative flex w-full touch-none items-center select-none",
  {
    variants: {
      variant: {
        default: "",
        xero: "",
        "quickbooks-online": "",
        freshbooks: "",
        "prime-themed": "",
        partner: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const trackVariants = cva(
  "rounded-button relative h-3 w-full grow overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-color-surface-dark",
        xero: "bg-color-surface-light",
        "quickbooks-online": "bg-color-surface-light",
        freshbooks: "bg-color-surface-light",
        "prime-themed": "bg-prime-background-darkest",
        partner: "bg-color-surface-dark",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const rangeVariants = cva("absolute h-full", {
  variants: {
    variant: {
      default: "bg-color-sidebar-surface",
      xero: "bg-[#1AB4D7]",
      "quickbooks-online": "bg-[#8db849]",
      freshbooks: "bg-[#0075dd]",
      "prime-themed": "bg-prime-primary",
      partner: "bg-color-button-primary",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const thumbVariants = cva(
  "rounded-button bg-color-surface-card ring-offset-background focus-visible:ring-ring flex h-6 w-9 cursor-grab items-center justify-center border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "",
        xero: "",
        "quickbooks-online": "",
        freshbooks: "",
        "prime-themed": "",
        partner: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
    VariantProps<typeof sliderVariants>,
    VariantProps<typeof trackVariants>,
    VariantProps<typeof rangeVariants> {
  className?: string;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({ className, variant, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn(sliderVariants({ variant }), className)}
    {...props}
  >
    <SliderPrimitive.Track className={trackVariants({ variant })}>
      <SliderPrimitive.Range className={rangeVariants({ variant })} />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb className={thumbVariants({ variant })}>
      <Tally3Icon size={16} />
    </SliderPrimitive.Thumb>
  </SliderPrimitive.Root>
));

Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
